<template>
  <div class="border-b border-border pb-4 last:border-0" :data-section-id="section.id">
    <div
      class="flex items-center justify-between cursor-pointer py-3 px-2 rounded-md hover:bg-muted/50 transition-colors duration-200"
      @click="toggleSection"
    >
      <div class="flex items-center gap-3">
        <!-- Add Icon Here -->
        <component
          v-if="iconComponent"
          :is="iconComponent"
          class="h-5 w-5 text-muted-foreground"
          aria-hidden="true"
        />
        <h3 class="font-semibold text-xl text-foreground">{{ section.title }}</h3>
        <span
          v-if="activeFilterCount > 0"
          class="px-2.5 py-1 rounded-full bg-primary text-sm font-medium text-primary-foreground"
        >
          {{ activeFilterCount }}
        </span>
      </div>
      <ChevronDownIcon
        class="h-5 w-5 text-muted-foreground transition-transform duration-200"
        :class="{ 'rotate-180': isExpanded }"
      />
    </div>

    <transition
      name="section-expand"
      @enter="startExpandTransition"
      @after-enter="finishExpandTransition"
      @before-leave="startCollapseTransition"
      @after-leave="finishCollapseTransition"
    >
      <div v-if="isExpanded" class="mt-4 space-y-4 px-2 overflow-hidden">
        <!-- Make/Model/Badge Filter Section -->
        <div v-if="section.id === 'make_model'" class="space-y-4">
          <slot name="make_model"></slot>
        </div>

        <!-- Other Filters -->
        <template v-for="filterName in section.filters" :key="filterName">
          <template v-if="!['make', 'model', 'badge'].includes(filterName)">
            <slot v-if="hasSlotContent(filterName)" :name="filterName"></slot>
            <template v-else>
              <component
                v-if="getFilter(filterName)"
                :is="getFilterComponent(getFilter(filterName))"
                :filter="getFilter(filterName)"
                :active-value="getActiveValueForFilter(filterName)"
                @update:value="(value: any) => updateFilter(filterName, value)"
                @clear="clearFilter(filterName)"
                :data-filter-name="filterName"
                class="filter-component"
              />
            </template>
          </template>
        </template>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  TagIcon,
  // BuildingStorefrontIcon, // Removed
  CurrencyDollarIcon,
  TruckIcon,
  // CogIcon, // Removed
  WrenchScrewdriverIcon,
  FireIcon,
  // UserGroupIcon, // Removed
  RectangleGroupIcon,
  PaintBrushIcon
} from '@heroicons/vue/24/outline'
import { CarIcon, CogIcon, Armchair } from 'lucide-vue-next'; // Import CarIcon, CogIcon & Armchair from Lucide
import { useFilters, type FilterSection, type Filter } from '~/layers/base/composables/useFilters'
import { resolveComponent, computed, ref, useSlots, getCurrentInstance } from 'vue'
import FilterSwitches from './FilterSwitches.vue'
import FilterColorSwatches from '~/layers/base/components/filters/FilterColorSwatches.vue'
import FilterSlider from '~/layers/base/components/filters/FilterSlider.vue'
import FilterCheckbox from '~/layers/base/components/filters/FilterCheckbox.vue'

// Import FilterSection type correctly - check path
import { type Filter as FilterType } from '~/layers/base/composables/useFilters'
// Define a local type for the section prop, making expanded optional
interface SectionProp {
  id: string;
  title: string;
  filters: string[];
  expanded?: boolean; // Make expanded optional
}

const props = defineProps<{
  section: SectionProp; // Use the local type
  activeFilters: Record<string, any>;
  isExpanded: boolean; // Add the new prop
}>()

const emit = defineEmits<{
  'toggle-section': [sectionId: string];
  'update-filter': [filterName: string, value: any];
  'clear-filter': [filterName: string];
}>()

const instance = getCurrentInstance();

const { getFilter } = useFilters()
const slots = useSlots()

// Computed property to determine the icon component based on section ID
const iconComponent = computed(() => {
  switch (props.section.id) {
    case 'search': return MagnifyingGlassIcon;
    case 'condition': return TagIcon;
    case 'make_model': return CarIcon; // Use CarIcon here
    case 'price': return CurrencyDollarIcon;
    case 'body': return TruckIcon;
    case 'transmission': return CogIcon; // Use CogIcon from Lucide here
    case 'drivetrain': return WrenchScrewdriverIcon;
    case 'fuel': return FireIcon;
    case 'seats': return Armchair; // Use Armchair icon here
    case 'doors': return RectangleGroupIcon;
    case 'colour': return PaintBrushIcon;
    default: return null; // Or a default icon
  }
});

// Animation methods for smooth expanding/collapsing
const startExpandTransition = (el: Element, done?: () => void) => {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = '0';
  htmlEl.style.overflow = 'hidden';

  // Force a reflow
  void htmlEl.offsetHeight;

  // Set the final height
  htmlEl.style.height = htmlEl.scrollHeight + 'px';

  // If done callback exists, call it after transition
  if (done) {
    setTimeout(done, 300);
  }
}

const finishExpandTransition = (el: Element) => {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = '';
  htmlEl.style.overflow = '';
}

const startCollapseTransition = (el: Element) => {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = htmlEl.scrollHeight + 'px';
  htmlEl.style.overflow = 'hidden';

  // Force a reflow
  void htmlEl.offsetHeight;

  // Set height to 0
  htmlEl.style.height = '0';
}

const finishCollapseTransition = (el: Element) => {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = '';
  htmlEl.style.overflow = '';
}

// Helper to check if a slot exists
const hasSlotContent = (name: string): boolean => {
  const result = !!slots[name];
  return result;
}

// Get count of active filters in this section
const activeFilterCount = computed(() => {
  return props.section.filters.reduce((count, filterName) => {
    const activeValue = props.activeFilters[filterName];

    // Handle array values directly
    if (Array.isArray(activeValue) && activeValue.length > 0) {
      return count + activeValue.length;
    }

    // Handle structured filter format {type: 'array', value: [...]}
    if (activeValue && typeof activeValue === 'object' &&
        activeValue.type === 'array' && Array.isArray(activeValue.value)) {
      return count + activeValue.value.length;
    }

    // Handle single string value from URL parameters
    if (typeof activeValue === 'string' && activeValue.trim() !== '') {
      return count + 1;
    }

    return count;
  }, 0);
})

// Get default value for a filter based on its type
const getDefaultValueForFilter = (filter: Filter | null): any => {
  if (!filter) return null

  if (filter.uiConfig?.displayType === 'favorites' || filter.type === 'boolean' || filter.name === 'favorites') {
    return false
  }

  if (filter.type === 'slider') {
    // Use the actual filter's data range as default, not hardcoded values
    if (filter.data && typeof filter.data === 'object' && 'min' in filter.data && 'max' in filter.data) {
      return { min: filter.data.min, max: filter.data.max }
    }
    // Fallback to hardcoded values only if filter data is not available
    return { min: 0, max: 100 }
  } else if (filter.type === 'text') {
    return ''
  }

  // Default for checkbox and other types
  return []
}

// Get the active value for a filter, ensuring correct type
const getActiveValueForFilter = (filterName: string): any => {
  const filter = getFilter(filterName)

  // Log for debugging
  if (filterName === 'price' || filterName === 'perweek') {
    console.log(`[FilterSection] getActiveValueForFilter(${filterName}):`, {
      filter,
      activeValue: props.activeFilters[filterName]
    });
  }

  // If the filter is undefined, return the default value
  if (props.activeFilters[filterName] === undefined) {
    if (filterName === 'price' || filterName === 'perweek') {
      console.log(`[FilterSection] ${filterName} is undefined in activeFilters, returning default:`, getDefaultValueForFilter(filter));
    }
    return getDefaultValueForFilter(filter)
  }

  // Special handling for favorites filter to ensure it's always a boolean
  if (filterName === 'favorites' || filter?.type === 'boolean' || filter?.uiConfig?.displayType === 'favorites') {
    return !!props.activeFilters[filterName] // Convert to boolean
  }

  // Handle range/slider filters
  if (filter?.type === 'slider' || ['price', 'perweek', 'kms', 'year'].includes(filterName)) {
    const value = props.activeFilters[filterName]
    if (value && typeof value === 'object') {
      if ('min' in value && 'max' in value) {
        if (filterName === 'price' || filterName === 'perweek') {
          console.log(`[FilterSection] ${filterName} has valid min/max:`, value);
        }
        return value
      }
      if (value.type === 'range' && 'min' in value && 'max' in value) {
        if (filterName === 'price' || filterName === 'perweek') {
          console.log(`[FilterSection] ${filterName} has valid range type:`, value);
        }
        return { min: value.min, max: value.max }
      }
    }
    // If the value is not a valid range object, return the default value
    if (filterName === 'price' || filterName === 'perweek') {
      console.log(`[FilterSection] ${filterName} value is not a valid range object:`, value);
      console.log(`[FilterSection] Returning default for ${filterName}:`, getDefaultValueForFilter(filter));
    }
    return getDefaultValueForFilter(filter)
  }

  // Handle array-based filters (including checkbox filters)
  if (filter?.type === 'checkbox' ||
      filter?.uiConfig?.displayType === 'checkbox' ||
      ['transmission', 'drivetrain', 'fuel', 'seats', 'doors', 'condition', 'colour'].includes(filterName)) {
    const value = props.activeFilters[filterName]

    // Handle raw array values
    if (Array.isArray(value)) {
      return value
    }

    // Handle new structured format with {type: "array", value: [...]}
    if (value && typeof value === 'object') {
      if (value.type === 'array' && Array.isArray(value.value)) {
        return value.value
      }

      // Handle URL parameter value that might be a string
      if (typeof value === 'string' || typeof value === 'number') {
        return [String(value)]
      }
    }

    // Handle single string value from URL (convert to array)
    if (typeof value === 'string') {
      return [value]
    }

    // Default empty array for null/undefined
    if (value === null || value === undefined) {
      return []
    }

    // If it's an object or other type, convert to empty array
    return []
  }

  // Return the value as is for other filters
  return props.activeFilters[filterName]
}

const toggleSection = () => {
  emit('toggle-section', props.section.id)
}

const updateFilter = (filterName: string, value: any) => {
  if (!filterName) {
    console.warn('Attempted to update filter without a name');
    return;
  }

  // For array-type filters, ensure the value is an array
  if (value === undefined) {
    const filter = getFilter(filterName);
    if (filter && (filter.type === 'checkbox' ||
        ['transmission', 'drivetrain', 'fuel', 'seats', 'doors', 'condition', 'colour'].includes(filterName))) {
      value = [];
    }
  }

  // Special handling for range filters
  if (['price', 'perweek', 'kms', 'year'].includes(filterName)) {
    if (!value || typeof value !== 'object') {
      const filter = getFilter(filterName);
      if (filter?.type === 'slider' && filter.data) {
        const filterData = filter.data as { min: number; max: number };
        value = {
          min: filterData.min,
          max: filterData.max
        };
      }
    }
  }

  console.log(`[FilterSection] Updating filter "${filterName}" with value:`, value);
  emit('update-filter', filterName, value);
}

const clearFilter = (filterName: string) => {
  emit('clear-filter', filterName)
}

// Map filter types to components
const getFilterComponent = (filter: Filter | null) => {
  let componentChoice: any = null;

  if (!filter) {
    console.error('[FilterSection] getFilterComponent called with null filter');
    return null;
  }

  // Log for debugging price/perweek filters
  if (filter.name === 'price' || filter.name === 'perweek') {
    console.log(`[FilterSection] getFilterComponent for ${filter.name}:`, filter);
  }

  // Special handling for favorites filter
  if (filter.name === 'favorites') {
    componentChoice = resolveComponent('FilterFavorites')
  }
  // Special handling for colour filter
  else if (filter.name === 'colour') {
    // Note: Colour is now handled by slot, but keep fallback?
    componentChoice = FilterColorSwatches
  }
  // Explicit check for condition filter
  else if (filter.name === 'condition') {
    // Note: Condition is now handled by slot, but keep fallback?
    componentChoice = FilterCheckbox;
  }
  // Handle range/slider filters first
  else if (filter.type === 'slider' || ['price', 'perweek', 'kms', 'year'].includes(filter.name)) {
    componentChoice = FilterSlider;
    if (filter.name === 'price' || filter.name === 'perweek') {
      console.log(`[FilterSection] Using FilterSlider for ${filter.name}`);
    }
  }
  // Use switches for specified filters
  else if (['transmission', 'drivetrain', 'fuel', 'seats', 'doors'].includes(filter.name)) {
     // Note: These are now handled by slot, but keep fallback?
    componentChoice = FilterSwitches
  }
  // First check for UI config display type
  else if (filter.uiConfig?.displayType === 'buttons') {
    componentChoice = resolveComponent('FilterButtons')
  } else if (filter.uiConfig?.displayType === 'grid') {
    componentChoice = resolveComponent('FilterMakeGrid')
  } else if (filter.uiConfig?.displayType === 'color-swatches') {
     // Note: Colour handled above and by slot
    componentChoice = FilterColorSwatches
  } else if (filter.uiConfig?.displayType === 'favorites') {
    componentChoice = resolveComponent('FilterFavorites')
  }
  // Then fall back to filter type
  else if (filter.type === 'text') {
    componentChoice = resolveComponent('FilterText')
  } else if (filter.type === 'boolean') {
    componentChoice = resolveComponent('FilterFavorites')
  }
  // Default to checkbox
  else {
      componentChoice = FilterCheckbox
  }

  return componentChoice;
}
</script>

<style scoped>
/* Tesla-inspired styling */
.filter-component {
  margin-top: 0.75rem;
}

/* Add subtle hover effect to the section header */
div[data-section-id] > div:first-child:hover {
  background-color: rgba(var(--muted), 0.2);
}

/* Animation styles for section expansion */
.section-expand-enter-active,
.section-expand-leave-active {
  transition: height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}

.section-expand-enter-from,
.section-expand-leave-to {
  height: 0;
  opacity: 0;
}
</style>
